import { IBookmark } from '../types/IBookmark';

export class DeduplicationService {
  /**
   * Remove duplicate bookmarks based on URL
   * Keeps the most recent bookmark if duplicates exist
   */
  static deduplicateBookmarks(bookmarks: IBookmark[]): IBookmark[] {
    const urlMap = new Map<string, IBookmark>();

    // Sort by addedOn descending to keep the most recent
    const sortedBookmarks = [...bookmarks].sort((a, b) =>
      (b.addedOn || 0) - (a.addedOn || 0)
    );

    // Keep only the first occurrence of each URL (most recent)
    for (const bookmark of sortedBookmarks) {
      if (bookmark.url && !urlMap.has(bookmark.url)) {
        urlMap.set(bookmark.url, bookmark);
      }
    }

    // Convert back to array and sort by addedOn ascending
    return Array.from(urlMap.values()).sort((a, b) =>
      (a.addedOn || 0) - (b.addedOn || 0)
    );
  }

  /**
   * Find duplicate bookmarks and return them for review
   */
  static findDuplicates(bookmarks: IBookmark[]): {
    unique: IBookmark[];
    duplicates: IBookmark[];
  } {
    const urlMap = new Map<string, IBookmark[]>();

    // Group bookmarks by URL
    for (const bookmark of bookmarks) {
      if (bookmark.url) {
        if (!urlMap.has(bookmark.url)) {
          urlMap.set(bookmark.url, []);
        }
        urlMap.get(bookmark.url)!.push(bookmark);
      }
    }

    const unique: IBookmark[] = [];
    const duplicates: IBookmark[] = [];

    // Process each URL group
    // @ts-ignore
    for (const [url, bookmarkGroup] of urlMap) {
      if (bookmarkGroup.length === 1) {
        // No duplicates, keep as is
        unique.push(bookmarkGroup[0]);
      } else {
        // Has duplicates, keep the most recent and mark others as duplicates
        const sortedGroup = bookmarkGroup.sort((a, b) =>
          (b.addedOn || 0) - (a.addedOn || 0)
        );
        unique.push(sortedGroup[0]); // Keep the most recent
        duplicates.push(...sortedGroup.slice(1)); // Mark the rest as duplicates
      }
    }

    return { unique, duplicates };
  }

  /**
   * Get statistics about duplicates
   */
  static getDuplicateStats(bookmarks: IBookmark[]): {
    total: number;
    unique: number;
    duplicates: number;
    duplicateUrls: string[];
  } {
    const { unique, duplicates } = this.findDuplicates(bookmarks);
    const duplicateUrls = [...new Set(duplicates.map(b => b.url).filter((url): url is string => url !== undefined))];

    return {
      total: bookmarks.length,
      unique: unique.length,
      duplicates: duplicates.length,
      duplicateUrls
    };
  }
}
